import React from "react";
import { useHistory } from "react-router-dom";
import Dropdown from "core/ui/Dropdown";
import { useState, useEffect, useContext } from "react";
import SessionContext from "SessionContext";
import CreateChannelDetails from "./royalty-create-channel";
import ConfirmSuccess from "../core/ui/confirm-success";
import ConfirmAction from "../core/ui/confirm-action";
import UnsavedChanges from "partials/unsaved-changes";

const channelConstructor = {
  indexValue: 0,
  channelName: "",
  channel_option_id: -1,
  effective_date: "",
  cost_per_record: "",
};
const selectionConstructor = { id: 0, name: "", description: "" };

function RoyaltyForm() {
  const history = useHistory();
  const session = useContext(SessionContext);
  
  const [source, setSource] = useState(selectionConstructor);
  const [product, setProduct] = useState(selectionConstructor);
  const [subProduct, setSubProduct] = useState(selectionConstructor);
  const [channels, setChannels] = useState([channelConstructor]);
  const [channelCounter, setChannelCounter] = useState(1);
  const [billingCodes, setBillingCodes] = useState([]);
  const [channelOptionList, setChannelOptionList] = useState(null);
  const [sourceOptionsList, setSourceOptionsList] = useState(null);
  const [productOptionsList, setProductOptionsList] = useState(null);
  const [subProductOptionsList, setSubProductOptionsList] = useState(null);
  const [billingCodesOptionsList, setBillingCodesOptionsList] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [qbDescription, setQbDescription] = useState("");
  const [confirmDeleteChannelModal, setConfirmDeleteChannelModal] = useState(false);
  const [channelIndexToDelete, setChannelIndexToDelete] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [channelHasChanges, setChannelHasChanges] = useState(false);

  useEffect(() => {
    if (source.name === null) {
      setSource(selectionConstructor);
    } else if (product.name === null) {
      setProduct(selectionConstructor);
    } else if (subProduct === null) {
      setSubProduct(selectionConstructor);
    }
  }, [source, product, subProduct]);

  useEffect(() => {
    loadSourceList();
    loadProductList();
    loadSubProductList();
    loadChannelOptionList();
    loadBillingCode();
    // eslint-disable-next-line
  }, []);

  function loadSourceList() {
    session
      .query("admin/get_royalty_sources")
      .then((response) => {
        setSourceOptionsList(response);
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  function loadChannelOptionList() {
    session
      .query("admin/get_channel_options")
      .then((response) => {
        setChannelOptionList(response);
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  function loadProductList() {
    session
      .query("admin/get_royalty_products")
      .then((response) => {
        setProductOptionsList(response);
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  function loadSubProductList() {
    session
      .query("admin/get_royalty_subproducts")
      .then((response) => {
        setSubProductOptionsList(response);
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  function loadBillingCode() {
    session
      .query("admin/get_billing_codes")
      .then((response) => {
        setBillingCodesOptionsList(response);
      })
  }

  const goBack = () => {
    history.push("/admin-royalty-pricing");
  };

  const handleSelectedSource = (selectedSource) => {
    if (selectedSource === null) {
      setSource(selectionConstructor);
    } else {
      setSource(selectedSource);
    }
  };

  const handleSelectedProduct = (selectedProduct) => {
    if (selectedProduct === null) {
      setProduct(selectionConstructor);
    } else {
      setProduct(selectedProduct);
    }
  };

  const handleSelectedSubProduct = (selectedSubProduct) => {
    if (selectedSubProduct === null) {
      setSubProduct(selectionConstructor);
    } else {
      setSubProduct(selectedSubProduct);
    }
  };

  const handleSelectedChannel = (selectedChannel) => {
    let newChannels = [...channels];
    newChannels[selectedChannel.indexValue] = selectedChannel;
    setChannels(newChannels);
  };

  const handleAddChannel = () => {
    setChannelCounter(channelCounter + 1);
    const lastIndex = channels[channels.length - 1].indexValue;
    const newChannel = { ...channelConstructor, indexValue: lastIndex + 1 };
    setChannels([...channels, newChannel]);
  };

  const askChannelDeleteConfirmation = (index) => {
    setChannelIndexToDelete(index);
    setConfirmDeleteChannelModal(true);
  }

  const handleRemoveChannel = (res) => {
    if(res && channelIndexToDelete) {
      let removed = channels.filter((el) => {
        return el.indexValue !== channelIndexToDelete;
      });
      let reDrawIndexes = removed.map((card, i) => {
        card.indexValue = i;
        return card;
      });
      if (channels.length !== 1) {
        setChannels(reDrawIndexes);
      }
      setConfirmDeleteChannelModal(false);
      setChannelIndexToDelete(null);
    } else {
      setConfirmDeleteChannelModal(false);
      setChannelIndexToDelete(null);
    }
  };

  const handleSelectedBillingCode = (selectedBillingCode) => {
    let newCodes = [...billingCodes];
    let isDuplicate = [];
    newCodes.forEach((code) => {
      if (code.id === selectedBillingCode.id) {isDuplicate.push(code)};
    });
    // don't add a duplicate option
    if(isDuplicate.length < 1) {
      newCodes.push(selectedBillingCode);
      setBillingCodes(newCodes);
    }
  };

  const handleDeleteBillingCode = (deleteCodeId) => {
    let newCodes = billingCodes.filter((code) => {
      return code.id !== deleteCodeId;
    });
    setBillingCodes(newCodes);
  };

  const handleQbDescription = (ev) => {
    ev.preventDefault();
    setQbDescription(ev.target.value);
  }

  const handleSave = () => {
    let finalChannels = channels.map((c) => {
      let prettyFormat = {
        channel_option_id: c.channel_option_id,
        effective_date: c.effective_date,
        cost_per_record: Number(c.cost_per_record),
      };
      return prettyFormat;
    });
    let finalBillingCodes = [];
    billingCodes.forEach((code) => {
      finalBillingCodes.push(code.id);
    });
    const createNew = {
      id: null,
      royalty_source_id: source.id,
      royalty_product_id: product.id,
      royalty_subproduct_id: subProduct.id,
      channel_options: finalChannels,
      billing_code_ids: finalBillingCodes,
      qb_description: qbDescription
    };

    session
      .command(
        "admin/create_royalty_cost",
        createNew,
        {},
        { defaultErrorHandling: false }
      )
      .then(() => {
        setHasUnsavedChanges(false);
        setShowSuccess(true);
        setTimeout(() => {
          goBack();
        }, 2000);
      })
      .catch((error) => {
        console.log("Royalty Cost Create Failed", JSON.stringify(error));
      });
  };

  const checkAllChannels = () => {
    let empties = channels.filter((ch) => {
      return ch.channel_option_id < 0;
    });
    return empties.length;
  };

  const checkAllValues = () => {
    if (
      source.name === "" ||
      product.name === "" ||
      subProduct.name === "" ||
      checkAllChannels() ||
      billingCodes.length < 1
    ) {
      return true;
    }
  };

  const handleChannelHasChanges = (_val) => {
    setChannelHasChanges(_val);
    return;
  }

  useEffect(() => {
    let has_channels = channels.filter((ch) => {
      return ch.channel_option_id > 0;
    });
    if (
      source.name !== "" ||
      product.name !== "" ||
      subProduct.name !== "" ||
      qbDescription !== "" ||
      billingCodes.length >= 1 ||
      has_channels.length ||
      channelHasChanges
    ) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  // eslint-disable-next-line
  }, [source, product, subProduct, qbDescription, billingCodes, channels, channelCounter, channelHasChanges])

  return (
    <div className="page admin-page royalty-form-page">
      <div className="list-header sticky-page-header">
        <button type="button" className="new-button" onClick={goBack}>
          <i className="bi bi-chevron-left"></i>{" "} Back
        </button>
        <h1>Add New Royalty Pricing</h1>
        <i className="spacer" />
      </div>
      <div className="main-section">
        <div className="left-selectors">
          <span className="label">Source</span>
          <Dropdown
            label="Select Source"
            options={sourceOptionsList}
            select={handleSelectedSource}
            disable={false}
            remove={false}
          />
          <span className="label">Product</span>
          <Dropdown
            label="Select Product"
            options={productOptionsList}
            select={handleSelectedProduct}
            disable={false}
            remove={false}
          />
          <span className="label">Sub Product</span>
          <Dropdown
            label="Select Sub Product"
            options={subProductOptionsList}
            select={handleSelectedSubProduct}
            disable={false}
            remove={false}
          />
          <span className="label">QB Description (Optional)</span>
          <textarea 
            className="text-input" 
            value={qbDescription}
            onChange={handleQbDescription}
          />
          <span className="label">Billing Code</span>
          <Dropdown
            label="Add Billing Code"
            options={billingCodesOptionsList}
            select={handleSelectedBillingCode}
            disable={false}
            remove={false}
            fieldToDisplay="description"
            fixedLabel
          />

          <div className="submit-cancel">
            <button
              type="button"
              className="btn btn-light btn-royalty"
              onClick={goBack}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-royalty promote"
              onClick={handleSave}
              disabled={checkAllValues()}
            >
              Submit
            </button>
          </div>
        </div>
        <div className="right-summary">
          <h5>Summary</h5>
          <div>
            Source: <strong>{source.name && source.name}</strong>
          </div>
          <div>
            Product: <strong>{product.name && product.name}</strong>
          </div>
          <div>
            Sub Product: <strong>{subProduct.name && subProduct.name}</strong>
          </div>
          <div>
            Billing Codes:
            <div className="billing-codes">
              {billingCodes.length > 0 &&
                billingCodes.map((bCode) => {
                  return (
                    <span className="selected-code" key={bCode.id}>
                      {bCode.description}
                      <button
                        type="button"
                        className="remove-billing-code"
                        onClick={() => handleDeleteBillingCode(bCode.id)}
                      >
                        <i className="bi bi-x-circle"></i>
                      </button>
                    </span>
                  );
                })}
            </div>
          </div>
          {subProduct &&
            channels.map((channel) => {
              return (
                <CreateChannelDetails
                  options={channelOptionList}
                  select={handleSelectedChannel}
                  index={channel.indexValue}
                  key={channel.indexValue}
                  remove={channel.indexValue >= 1 ? askChannelDeleteConfirmation : null}
                  channelHasChanges={handleChannelHasChanges}
                />
              );
            })}
          {subProduct && (
            <div className="channel-selector">
              <button type="button" onClick={handleAddChannel}>
                Add Channel
              </button>
            </div>
          )}
        </div>
      </div>
      <ConfirmSuccess show={showSuccess} label="Saved!" />
      <ConfirmAction 
        show={confirmDeleteChannelModal}
        title="Confirm Delete Channel"
        bodyText={
          <div className="text-center">
            <h6>Click <b>Continue</b> to delete this channel</h6> 
            <div>or</div>
            <h6>Click <b>Cancel</b> to go back to editing.</h6>
          </div>}
        onConfirm={handleRemoveChannel}
      />
      <UnsavedChanges hasUnsavedChanges={hasUnsavedChanges} />
    </div>
  );
}

export default RoyaltyForm;
